/**
 * Как принятые из документов строки ложатся в дело. Одно правило и для
 * загрузки в самих разделах, и для общего экрана в «Должнике».
 */

import type { CaseProfile, Creditor, ObligatoryPayment } from '@bd/domain';
import type { CreditBureauRef } from '../../../api.js';
import type { AcceptedProperty } from './PropertyDocumentsImport.js';

/**
 * Кредиторы: пустые строки, заведённые впрок, уступают место строкам из документов.
 * `overdue` — просрочка по УИД из повторно загруженного отчёта: дополняет строки,
 * внесённые до 26.10.2026 без неё; вписанное юристом не переписывается.
 */
export function withCreditors(p: CaseProfile, rows: Creditor[], overdue: Record<string, number> = {}, bureaus: CreditBureauRef[] = []): CaseProfile {
  const kept = p.creditors
    .filter((c) => c.name.trim() || c.amountDebt > 0)
    .map((c) => (c.sourceUid && c.amountOverdue === undefined && c.sourceUid in overdue ? { ...c, amountOverdue: overdue[c.sourceUid] } : c));
  return { ...p, legal: bureaus.reduce(withBureau, p.legal), creditors: [...kept, ...rows] };
}

/**
 * Бюро отчёта — в «Процессуальное», в пустое место: сначала первое бюро,
 * отчёт другого бюро — вторым (01.11.2026). Выбранное юристом не меняется.
 */
function withBureau(legal: CaseProfile['legal'], bureau: CreditBureauRef): CaseProfile['legal'] {
  const first = legal.creditBureau;
  if (!first.id && !first.name.trim()) {
    return { ...legal, creditBureau: { id: bureau.id, name: bureau.name }, creditReportKind: legal.creditReportKind || bureau.reportKind };
  }
  const second = legal.secondCreditBureau;
  if (first.id === bureau.id || first.name === bureau.name || second?.id || second?.name.trim()) return legal;
  return { ...legal, secondCreditBureau: { id: bureau.id, name: bureau.name }, secondCreditReportKind: legal.secondCreditReportKind || bureau.reportKind };
}

/** Штрафы и налоги по постановлениям ФССП — в «Обязательные платежи» (ответ юриста 28.09.2026). */
export function withObligatory(p: CaseProfile, payments: ObligatoryPayment[]): CaseProfile {
  return { ...p, obligatoryPayments: [...p.obligatoryPayments, ...payments] };
}

/**
 * Имущество: то же для строк описи; недвижимость или транспорт из выписок —
 * имущество зарегистрировано. Отмеченные сделки из выписки ФНС — в блок
 * «Сделки», и признак «были сделки» включается (26.10.2026).
 */
export function withProperty(p: CaseProfile, accepted: AcceptedProperty): CaseProfile {
  const deals = accepted.deals ?? [];
  return {
    ...p,
    deals: deals.length
      ? { hasDeals: true, items: [...p.deals.items.filter((d) => d.subject.trim()), ...deals] }
      : p.deals,
    property: {
      ...p.property,
      hasProperty: p.property.hasProperty || accepted.realty.length > 0 || accepted.vehicles.length > 0,
      realty: [...p.property.realty.filter((r) => r.cadastralNumber.trim() || r.address.trim()), ...accepted.realty],
      vehicles: [...p.property.vehicles.filter((v) => v.model.trim()), ...accepted.vehicles],
      accounts: [...p.property.accounts.filter((a) => a.bankName.trim() || a.balance > 0), ...accepted.accounts],
    },
  };
}

/** Уже внесённое — чтобы разбор не предлагал его второй раз: УИД договоров и ключи имущества. */
export function existingKeys(p: CaseProfile): string[] {
  return [
    ...p.creditors.map((c) => c.sourceUid).filter((uid): uid is string => Boolean(uid)),
    ...p.property.realty.filter((r) => r.cadastralNumber).map((r) => `realty:${r.cadastralNumber}`),
    ...p.property.vehicles.flatMap((v) => [v.registrationNumber, v.identificationNumber].filter(Boolean).map((k) => `vehicle:${k}`)),
    ...p.property.accounts.filter((a) => a.accountNumber).map((a) => `account:${a.accountNumber}`),
    ...p.deals.items.map((d) => d.sourceKey).filter((key): key is string => Boolean(key)),
  ];
}
